import { useEffect, useState, type ReactNode } from 'react';
import { fetchMaterialById } from '../../../../features/materials/api';
import type { DialogueLine } from '../../../../features/scenarios/types';
import type { VoiceLineTiming } from '../../../../features/scenarios/api';
import { formatVoiceSec, readVoiceLineTimings } from '../voiceLines';
import styles from '../ScenarioGenerator.module.css';
import { Tooltip } from '../../../common/Tooltip';
import { CopyTextButton } from './CopyTextButton';

const muted = 'rgba(245, 241, 230, 0.5)';

/** ナレーション素材の行の時刻 (generation_metadata.voice_lines)。素材が変わったら取り直す。 */
function useVoiceLineTimings(materialId: string | null | undefined): VoiceLineTiming[] {
  const [timings, setTimings] = useState<VoiceLineTiming[]>([]);
  useEffect(() => {
    if (!materialId) {
      setTimings([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const material = await fetchMaterialById(materialId);
        if (!cancelled) setTimings(material ? readVoiceLineTimings(material as { generation_metadata?: Record<string, unknown> }) : []);
      } catch {
        if (!cancelled) setTimings([]);
      }
    })();
    return () => { cancelled = true; };
  }, [materialId]);
  return timings;
}

/**
 * シーンのナレーションの行一覧 (SceneNarrationBox の本文欄・歌モノ以外のすべてのシナリオ)。
 * 行ごとに 番号・話者・本文・開始秒 (ナレーション素材の voice_lines) と「この行だけ撮り直す」を出す (行が複数あるときだけ)。
 * ナレーション素材は行を台本の順に 1 本につないだものなので、再生は結果エリアのシーン単位の再生に任せる (行ごとの再生は出さない)。
 * 話者付きの行の編集は台本欄 (SceneDetailColumn) で行う。実写のナレーター行 (narration 欄) だけは editableLineId で
 * 鉛筆を出し、編集中は editor をその行の位置に差し込む。本文が空の行は「（ナレーションなし）」と出す。
 */
export function NarrationVoiceLines({
  lines,
  materialId,
  retakingLineId,
  disabled,
  onRetake,
  editableLineId,
  onEditLine,
  onClearLine,
  editingLineId,
  editor,
}: {
  lines: DialogueLine[];
  /** 再生に使うナレーション素材 (除外中は null) */
  materialId: string | null | undefined;
  /** 撮り直し中の行 */
  retakingLineId?: string | null;
  disabled?: boolean;
  onRetake?: (lineId: string) => void;
  /** 鉛筆で編集できる行 (実写のナレーター行) */
  editableLineId?: string | null;
  onEditLine?: (lineId: string) => void;
  /** 鉛筆の行の本文を空にする (実写のナレーター行の「クリア」)。未指定ならボタンを出さない */
  onClearLine?: (lineId: string) => void;
  /** 編集中の行 (editor をその行の代わりに出す) */
  editingLineId?: string | null;
  editor?: ReactNode;
}) {
  const timings = useVoiceLineTimings(materialId);
  const timingOf = (lineId: string | undefined) => (lineId ? timings.find((t) => t.line_id === lineId) : undefined);

  if (lines.length === 0) {
    return (
      <div className={`${styles.narrationTextDisplay} ${styles.narrationTextEmpty}`}>
        <img src="/microphone_1.svg" alt="ナレーション" className={styles.sceneIcon} />
        <span>（ナレーションなし）</span>
      </div>
    );
  }

  // ナレーターだけのシーン (実写の大半) は従来のナレーション欄と同じ見た目 (マイク + 本文 + 鉛筆) にし、
  // 番号と話者名は複数の行・複数の話者があるときだけ出す
  const showIndex = lines.length > 1;
  // 行ごとの操作 (開始秒・撮り直し) も行が複数あるときだけ。1 行なら「このナレーションだけ生成」と同じ操作になる
  const showLineControls = lines.length > 1;
  const speakerLabels = new Set(lines.map((l) => l.speaker?.label || 'ナレーター'));
  const showSpeaker = speakerLabels.size > 1 || lines.some((l) => l.speaker?.kind !== 'narration' && !!l.speaker?.character_key);
  const controlsFor = (line: DialogueLine, idx: number, timing: VoiceLineTiming | undefined, lineStale: boolean) => {
    const nodes = [] as ReactNode[];
    if (timing) nodes.push(<span key="t" style={{ fontSize: 11, color: muted, whiteSpace: 'nowrap' }}>{`${formatVoiceSec(timing.startSec)}〜`}</span>);
    if (lineStale) nodes.push(<span key="s" className={styles.motionSyncStaleNotice} data-testid={`narration-voice-line-stale-${idx}`}>ナレーションの再生成が必要</span>);
    if (onRetake && timing && line.line_id) {
      nodes.push(
        <button
          key="r"
          type="button"
          className="btn-modal-secondary-sm"
          disabled={disabled}
          onClick={() => onRetake(line.line_id as string)}
          title="この行だけ読み直し、他の行はそのまま使ってつなぎ直します"
        >
          {retakingLineId === line.line_id ? '撮り直し中…' : 'この行だけ撮り直す'}
        </button>,
      );
    }
    return nodes;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }} data-testid="narration-voice-lines">
      {lines.map((line, idx) => {
        const timing = timingOf(line.line_id);
        const empty = !line.text.trim();
        // 行の鮮度 (表示だけ): ナレーション素材に無い行・本文が変わった行は再生成が必要 (本文の無い行は対象外)
        // 旧経路 (1 声のナレーション生成) の素材は行の時刻を持たないので、行の鮮度は判定しない (シーン側の鮮度だけ)
        const lineStale = !empty && !!materialId && timings.length > 0 && (!timing || timing.text !== line.text);
        if (editingLineId && line.line_id === editingLineId && editor) {
          return <div key={line.line_id ?? idx} data-testid={`narration-voice-line-${idx}`}>{editor}</div>;
        }
        const editable = !!onEditLine && !!line.line_id && line.line_id === editableLineId;
        const controls = showLineControls ? controlsFor(line, idx, timing, lineStale) : (lineStale ? controlsFor(line, idx, undefined, true) : []);
        return (
          <div
            key={line.line_id ?? idx}
            data-testid={`narration-voice-line-${idx}`}
            className={`${styles.narrationTextDisplay} ${empty ? styles.narrationTextEmpty : ''}`}
            style={{ marginTop: 0, alignItems: 'flex-start', flexWrap: 'wrap' }}
          >
            {showIndex && <span style={{ fontSize: 11, color: muted, minWidth: 14, lineHeight: '20px' }}>{idx + 1}</span>}
            {showSpeaker
              ? <span style={{ fontSize: 12, color: muted, whiteSpace: 'nowrap', lineHeight: '20px' }}>{line.speaker?.label || 'ナレーター'}</span>
              : <img src="/microphone_1.svg" alt="ナレーション" className={styles.sceneIcon} />}
            <span style={{ flex: 1, minWidth: 160 }}>
              {empty ? '（ナレーションなし）' : line.text}
            </span>
            {/* 鉛筆・コピー・クリアは本文 span の外に横並びで置く (本文の中に入れると display:flex の
                ボタンが次の行へ落ち、セリフ / テロップ欄 (sceneScript) の並びと揃わないため。2026-10-22) */}
            {editable && (
              <button
                type="button"
                className={styles.fieldEditButton}
                onClick={() => onEditLine?.(line.line_id as string)}
                title="ナレーションを編集"
              >
                <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
              </button>
            )}
            {editable && <CopyTextButton text={line.text} label="ナレーション" />}
            {editable && onClearLine && !empty && (
              <Tooltip content="ナレーションをクリア">
                <button
                  type="button"
                  className={styles.fieldEditButton}
                  onClick={() => onClearLine(line.line_id as string)}
                  aria-label="ナレーションをクリア"
                  disabled={disabled}
                >
                  <img src="/delete_2.svg" alt="" className={styles.fieldEditIcon} />
                </button>
              </Tooltip>
            )}
            {controls.length > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
                {controls}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
